import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  Fab,
  Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CampaignIcon from '@mui/icons-material/Campaign';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MapIcon from '@mui/icons-material/Map';
import PhonelinkEraseIcon from '@mui/icons-material/PhonelinkErase';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import {
  Client,
  ClientTag,
  MyCampaign,
  fetchClients,
  fetchTags,
  bulkTagClients,
  createTag,
  deleteClient,
  setClientEngagementActive,
  fetchMyCampaigns,
  fetchCampaignEnrollment,
  setCampaignEnrollment,
  fetchWhatsAppCloudStatus,
  fetchAgentSettings,
  fetchCampaigns,
  STAGES,
  STAGE_LABELS,
  STAGE_COLORS,
  TAG_COLORS,
  WHATSAPP_UNREACHABLE_HINT,
  Stage,
} from '../services/api';
import { useMobileMask } from '../context/MobileMaskContext';
import {
  evaluateSevenDayReadiness,
  engagementDaysFromCampaignRows,
  readinessWarningText,
  CampaignReadiness,
} from '../utils/campaignReadiness';
import QuickCreateClient from '../components/QuickCreateClient';
import WhatsAppComposeDialog from '../components/WhatsAppComposeDialog';
import ImportContactsDialog from '../components/ImportContactsDialog';
import ClientsMap from '../components/ClientsMap';
import UploadFileIcon from '@mui/icons-material/UploadFile';

const PAGE_SIZE = 20;

/**
 * 7 squares, one per engagement day, filled up to the last day already sent —
 * e.g. Day 3 sent (Day 4 not yet) → first 3 squares coloured. Dimmed when the
 * client's engagement is toggled off.
 */
function EngagementDayProgress({ lastSentDay, active }: { lastSentDay: number; active: boolean }) {
  const done = Math.max(0, Math.min(7, lastSentDay));
  return (
    <Tooltip title={done > 0 ? `7-Day Engagement: Day ${done} of 7 sent` : '7-Day Engagement: no messages sent yet'}>
      <Box sx={{ display: 'flex', gap: '3px', mt: 0.25, ml: 0.5, opacity: active ? 1 : 0.35 }}>
        {Array.from({ length: 7 }, (_, i) => (
          <Box
            key={i}
            sx={{
              width: 9,
              height: 9,
              borderRadius: '2px',
              // All 7 squares always render: sent days solid orange, unsent
              // days dim gray so the bar reads as "N of 7" at a glance.
              // Literal mid-gray on purpose: tokens composed from
              // --color-text-rgb (space-separated triplet) are invalid inside
              // rgba(...) and render as NO background at all.
              bgcolor: i < done ? '#ed8936' : 'rgba(128, 128, 128, 0.5)',
            }}
          />
        ))}
      </Box>
    </Tooltip>
  );
}

export default function ClientList({
  isAdmin = false,
  adminMode = false,
  spotlight,
}: {
  isAdmin?: boolean;
  adminMode?: boolean;
  /**
   * Guided-tour mode for the Why Super Connector page:
   * 'add-client' pops the (blinking) Add Client drawer, 'tag-filter' auto-opens
   * the (blinking) Tag filter dropdown.
   */
  spotlight?: 'add-client' | 'tag-filter';
}) {
  const navigate = useNavigate();
  const { formatMobile } = useMobileMask();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [clients, setClients] = useState<Client[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterStage, setFilterStage] = useState<string>('');
  // Filter by campaign enrollment ('' = all, SEVEN_DAY, or a custom campaign name).
  // Applied server-side so it composes with pagination.
  const [filterCampaign, setFilterCampaign] = useState<string>('');
  // Sub-filter of the 7-Day Engagement campaign filter: '' = any day, '1'-'7' =
  // clients sitting exactly ON that day (day N sent, N+1 not yet). Server-side.
  const [filterDay, setFilterDay] = useState<string>('');
  // Filter by tags (match ANY), applied server-side. All this agent's tags for the dropdown.
  const [availableTags, setAvailableTags] = useState<ClientTag[]>([]);
  const [filterTags, setFilterTags] = useState<string[]>([]);
  // Current page (0-based). Pagination is server-side, PAGE_SIZE rows per page.
  const [page, setPage] = useState(0);
  // List (paginated cards) vs Map (geo plot of the same filtered set).
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const showMap = isAdmin && viewMode === 'map';
  const [showCreate, setShowCreate] = useState(spotlight === 'add-client');
  const [showImport, setShowImport] = useState(false);
  // Controlled open state for the tag-filter dropdown, only used while the
  // 'tag-filter' spotlight auto-shows it.
  const [tagMenuOpen, setTagMenuOpen] = useState(false);
  // Quick "+ Add tag" from the tag-filter dropdown (mirrors Settings → Manage Tags).
  const [showAddTag, setShowAddTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState<string>(TAG_COLORS[0]);
  const [addTagBusy, setAddTagBusy] = useState(false);
  const [addTagError, setAddTagError] = useState<string | null>(null);
  // Confirm dialog for the per-row engagement toggle. Toggling is destructive
  // (kicks off / pauses outbound automation), so we always confirm.
  const [engagementConfirm, setEngagementConfirm] = useState<{ client: Client; nextActive: boolean } | null>(null);
  const [engagementBusy, setEngagementBusy] = useState(false);
  // Delete confirmation — deletion soft-defuncts the row server-side, but we
  // still gate it behind a confirm so an accidental tap can't wipe a lead.
  const [deleteConfirm, setDeleteConfirm] = useState<Client | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Row-level "Send WhatsApp" target. Opens the shared compose dialog which
  // handles template pick → edit → send-via-bridge + activity logging.
  const [waTarget, setWaTarget] = useState<Client | null>(null);

  // Sentinel value for the "+ Add tag" row inside the tag-filter dropdown.
  const ADD_TAG = '__add_tag__';

  // ── Multi-select / bulk enrol into a campaign ──
  const SEVEN_DAY = '__7day__';
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [myCampaigns, setMyCampaigns] = useState<MyCampaign[]>([]);
  const [enrollTarget, setEnrollTarget] = useState<string>(SEVEN_DAY);
  const [enrollConfirm, setEnrollConfirm] = useState(false);
  const [enrollBusy, setEnrollBusy] = useState(false);
  // ── Bulk tag the selected clients (additive) ──
  const [bulkTags, setBulkTags] = useState<string[]>([]);
  const [bulkTagBusy, setBulkTagBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  const [sevenDayReadiness, setSevenDayReadiness] = useState<CampaignReadiness>({ canActivate: true, reasons: [] });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [cardMenu, setCardMenu] = useState<{ client: Client; anchor: HTMLElement } | null>(null);

  const activeFilterCount = [filterType, filterStage, filterCampaign, filterDay, filterTags.length > 0 ? 'tags' : '']
    .filter(Boolean).length;

  const enterSelectMode = () => {
    setSelectMode(true);
    if (myCampaigns.length === 0) {
      fetchMyCampaigns().then(setMyCampaigns).catch(() => setMyCampaigns([]));
    }
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setBulkTags([]);
  };
  const toggleSelected = (id: string) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const enrollTargetLabel = enrollTarget === SEVEN_DAY
    ? '7-Day Engagement'
    : enrollTarget;

  const runEnroll = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setEnrollBusy(true);
    try {
      if (enrollTarget === SEVEN_DAY) {
        await Promise.all(ids.map((id) => setClientEngagementActive(id, true)));
        setClients((prev) => prev.map((c) => (selectedIds.has(c.Id) ? { ...c, EngagementActive: true } : c)));
      } else {
        // Per-campaign enrollment replaces the whole set, so merge with current.
        const current = await fetchCampaignEnrollment(enrollTarget);
        const merged = Array.from(new Set([...current.clientIds, ...ids]));
        await setCampaignEnrollment(enrollTarget, merged);
      }
      setToast({ msg: `${ids.length} client${ids.length === 1 ? '' : 's'} enrolled into ${enrollTargetLabel}`, severity: 'success' });
      setEnrollConfirm(false);
      exitSelectMode();
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : 'Failed to enrol clients', severity: 'error' });
    } finally {
      setEnrollBusy(false);
    }
  };

  const runCreateTag = async () => {
    const name = newTagName.trim();
    if (!name) return;
    setAddTagBusy(true);
    setAddTagError(null);
    try {
      await createTag({ Name: name, Color: newTagColor });
      const tags = await fetchTags();
      setAvailableTags(tags);
      setShowAddTag(false);
      setToast({ msg: `Tag "${name}" created`, severity: 'success' });
    } catch (e) {
      setAddTagError(e instanceof Error ? e.message : 'Failed to create tag');
    } finally {
      setAddTagBusy(false);
    }
  };

  const runBulkTag = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || bulkTags.length === 0) return;
    setBulkTagBusy(true);
    try {
      await bulkTagClients(ids, bulkTags, 'add');
      const tagNames = bulkTags.map((id) => tagById.get(id)?.Name).filter(Boolean).join(', ');
      setToast({ msg: `Tagged ${ids.length} client${ids.length === 1 ? '' : 's'} with ${tagNames}`, severity: 'success' });
      exitSelectMode();
      loadClients();
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : 'Failed to tag clients', severity: 'error' });
    } finally {
      setBulkTagBusy(false);
    }
  };

  // Populate the campaign list (for the enrol dropdown + the campaign filter).
  useEffect(() => {
    fetchMyCampaigns().then(setMyCampaigns).catch(() => setMyCampaigns([]));
  }, []);

  // 7-Day engagement toggle readiness (same rules as Settings / ClientDetail).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cloudStatus, settings, camps] = await Promise.all([
          fetchWhatsAppCloudStatus().catch(() => null),
          fetchAgentSettings().catch(() => null),
          fetchCampaigns().catch(() => null),
        ]);
        if (cancelled) return;
        const usingCloud = cloudStatus?.provider === 'cloud';
        const engagementRows = camps?.grouped?.['7-Day Engagement'] ?? [];
        setSevenDayReadiness(evaluateSevenDayReadiness({
          days: engagementDaysFromCampaignRows(engagementRows),
          agentSettings: settings,
          usingCloud: !!usingCloud,
          templates: cloudStatus?.templates ?? [],
        }));
      } catch {
        if (!cancelled) setSevenDayReadiness({ canActivate: true, reasons: [] });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Populate the tag list for the tag filter dropdown.
  useEffect(() => {
    fetchTags().then(setAvailableTags).catch(() => setAvailableTags([]));
  }, []);
  const tagById = React.useMemo(() => new Map(availableTags.map((t) => [t.Id, t])), [availableTags]);

  // Spotlight: once tags are loaded, auto-show the tag dropdown after a beat so
  // the user's eye lands on it (the dropdown only renders when tags exist).
  useEffect(() => {
    if (spotlight !== 'tag-filter' || availableTags.length === 0) return;
    const timer = setTimeout(() => setTagMenuOpen(true), 600);
    return () => clearTimeout(timer);
  }, [spotlight, availableTags.length]);

  // Select-all operates on the current page's rows.
  const allSelected = clients.length > 0 && clients.every((c) => selectedIds.has(c.Id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(clients.map((c) => c.Id)));

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Reset to the first page whenever the active filters/search change so we
  // never land on an out-of-range page.
  useEffect(() => {
    setPage(0);
  }, [search, filterType, filterStage, filterCampaign, filterDay, filterTags, adminMode]);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchClients({
        search: search || undefined,
        type: filterType || undefined,
        stage: filterStage || undefined,
        campaign: filterCampaign || undefined,
        engagementDay: filterCampaign === SEVEN_DAY && filterDay ? Number(filterDay) : undefined,
        tags: filterTags.length > 0 ? filterTags : undefined,
        adminMode,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setClients(result.clients);
      setTotal(result.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, filterType, filterStage, filterCampaign, filterDay, filterTags, adminMode, page]);

  useEffect(() => {
    const debounce = setTimeout(loadClients, 300);
    return () => clearTimeout(debounce);
  }, [loadClients]);

  const handleCreated = () => {
    setShowCreate(false);
    loadClients();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h5" fontWeight={700}>Clients</Typography>
          {isAdmin && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={(_, v) => {
                if (!v) return;
                if (v === 'map' && selectMode) exitSelectMode();
                setViewMode(v);
              }}
              sx={{
                '& .MuiToggleButton-root': { textTransform: 'none', px: isMobile ? 0.75 : 1.25, py: 0.4 },
                '& .Mui-selected': { color: '#ed8936 !important', bgcolor: '#ed893615 !important' },
              }}
            >
              <ToggleButton value="list">
                <FormatListBulletedIcon fontSize="small" sx={{ mr: isMobile ? 0 : 0.5 }} />
                {!isMobile && 'List'}
              </ToggleButton>
              <ToggleButton value="map">
                <MapIcon fontSize="small" sx={{ mr: isMobile ? 0 : 0.5 }} />
                {!isMobile && 'Map'}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          {!showMap && (
            isMobile ? (
              <IconButton
                size="small"
                onClick={selectMode ? exitSelectMode : enterSelectMode}
                sx={{ color: selectMode ? '#64748b' : '#ed8936', border: '1px solid', borderColor: selectMode ? '#64748b' : '#ed8936' }}
                title={selectMode ? 'Cancel' : 'Select'}
              >
                <CampaignIcon fontSize="small" />
              </IconButton>
            ) : (
              <Button
                variant={selectMode ? 'contained' : 'outlined'}
                startIcon={<CampaignIcon />}
                onClick={selectMode ? exitSelectMode : enterSelectMode}
                sx={selectMode
                  ? { bgcolor: '#64748b', '&:hover': { bgcolor: '#475569' }, textTransform: 'none' }
                  : { borderColor: '#ed8936', color: '#ed8936', '&:hover': { borderColor: '#dd7826', bgcolor: '#ed893611' }, textTransform: 'none' }}
              >
                {selectMode ? 'Cancel' : 'Select'}
              </Button>
            )
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!selectMode && (
            isMobile ? (
              <IconButton
                size="small"
                onClick={() => setShowImport(true)}
                sx={{ color: '#ed8936', border: '1px solid #ed8936' }}
                title="Import"
              >
                <UploadFileIcon fontSize="small" />
              </IconButton>
            ) : (
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => setShowImport(true)}
                sx={{ borderColor: '#ed8936', color: '#ed8936', '&:hover': { borderColor: '#dd7826', bgcolor: '#ed893611' }, textTransform: 'none' }}
              >
                Import
              </Button>
            )
          )}
          {!isMobile && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreate(true)}
              sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
            >
              Add Client
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search by name, phone or company..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: isMobile ? 0 : 200, width: isMobile ? '100%' : undefined }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'var(--color-text-faint)' }} /></InputAdornment>,
          }}
        />
        {isMobile ? (
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setFiltersOpen(true)}
            sx={{
              textTransform: 'none',
              borderColor: activeFilterCount ? '#ed8936' : 'var(--color-border)',
              color: activeFilterCount ? '#ed8936' : 'var(--color-text-subtle)',
              whiteSpace: 'nowrap',
            }}
          >
            Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}
          </Button>
        ) : (
          <>
        <Select
          size="small"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          displayEmpty
          sx={{ minWidth: 100 }}
        >
          <MenuItem value="">All Types</MenuItem>
          <MenuItem value="buyer">Buyer</MenuItem>
          <MenuItem value="seller">Seller</MenuItem>
        </Select>
        <Select
          size="small"
          value={filterStage}
          onChange={(e) => setFilterStage(e.target.value)}
          displayEmpty
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">All Stages</MenuItem>
          {STAGES.map((s) => (
            <MenuItem key={s} value={s}>{STAGE_LABELS[s]}</MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={filterCampaign}
          onChange={(e) => {
            setFilterCampaign(e.target.value);
            if (e.target.value !== SEVEN_DAY) setFilterDay('');
          }}
          displayEmpty
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All Campaigns</MenuItem>
          <MenuItem value={SEVEN_DAY}>7-Day Engagement</MenuItem>
          {myCampaigns.map((c) => (
            <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem>
          ))}
        </Select>
        {filterCampaign === SEVEN_DAY && (
          <Select
            size="small"
            value={filterDay}
            onChange={(e) => setFilterDay(e.target.value)}
            displayEmpty
            sx={{ minWidth: 110 }}
          >
            <MenuItem value="">All Days</MenuItem>
            {Array.from({ length: 7 }, (_, i) => (
              <MenuItem key={i + 1} value={String(i + 1)}>On Day {i + 1}</MenuItem>
            ))}
          </Select>
        )}
        {(
          <Select
            size="small"
            multiple
            value={filterTags}
            onChange={(e) => {
              const vals = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
              if (vals.includes(ADD_TAG)) {
                setNewTagName('');
                setNewTagColor(TAG_COLORS[0]);
                setAddTagError(null);
                setShowAddTag(true);
                return;
              }
              setFilterTags(vals);
            }}
            displayEmpty
            {...(spotlight === 'tag-filter'
              ? {
                  open: tagMenuOpen,
                  onOpen: () => setTagMenuOpen(true),
                  onClose: () => setTagMenuOpen(false),
                }
              : {})}
            sx={{
              minWidth: 140,
              ...(spotlight === 'tag-filter' && {
                animation: 'tagSpotlightBlink 1.3s ease-in-out infinite',
                '@keyframes tagSpotlightBlink': {
                  '0%, 100%': { boxShadow: '0 0 0 0 rgba(237, 137, 54, 0.75)' },
                  '50%': { boxShadow: '0 0 0 7px rgba(237, 137, 54, 0)' },
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ed8936', borderWidth: 2 },
              }),
            }}
            renderValue={(selected) =>
              selected.length === 0 ? (
                'All Tags'
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => {
                    const t = tagById.get(id);
                    if (!t) return null;
                    return (
                      <Chip
                        key={id}
                        label={t.Name}
                        size="small"
                        sx={{ height: 18, fontSize: '0.65rem', bgcolor: `${t.Color || '#ed8936'}22`, color: t.Color || '#ed8936' }}
                      />
                    );
                  })}
                </Box>
              )
            }
          >
            {availableTags.map((t) => (
              <MenuItem key={t.Id} value={t.Id}>
                <Checkbox
                  size="small"
                  checked={filterTags.includes(t.Id)}
                  sx={{ p: 0.5, mr: 1, '&.Mui-checked': { color: t.Color || '#ed8936' } }}
                />
                <Chip
                  label={t.Name}
                  size="small"
                  sx={{ bgcolor: `${t.Color || '#ed8936'}22`, color: t.Color || '#ed8936', fontWeight: 700 }}
                />
              </MenuItem>
            ))}
            <MenuItem value={ADD_TAG} sx={{ borderTop: '1px solid var(--color-border)', color: '#ed8936', fontWeight: 600 }}>
              <AddIcon fontSize="small" sx={{ mr: 1 }} />
              Add tag
            </MenuItem>
          </Select>
        )}
          </>
        )}
      </Box>

      <Drawer
        anchor="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 12, borderTopRightRadius: 12, p: 2, bgcolor: 'var(--color-surface)', maxHeight: '80vh' } }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Filters</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select label="Type" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="buyer">Buyer</MenuItem>
              <MenuItem value="seller">Seller</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Stage</InputLabel>
            <Select label="Stage" value={filterStage} onChange={(e) => setFilterStage(e.target.value)}>
              <MenuItem value="">All Stages</MenuItem>
              {STAGES.map((s) => (
                <MenuItem key={s} value={s}>{STAGE_LABELS[s]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Campaign</InputLabel>
            <Select
              label="Campaign"
              value={filterCampaign}
              onChange={(e) => {
                setFilterCampaign(e.target.value);
                if (e.target.value !== SEVEN_DAY) setFilterDay('');
              }}
            >
              <MenuItem value="">All Campaigns</MenuItem>
              <MenuItem value={SEVEN_DAY}>7-Day Engagement</MenuItem>
              {myCampaigns.map((c) => (
                <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {filterCampaign === SEVEN_DAY && (
            <FormControl size="small" fullWidth>
              <InputLabel>Day</InputLabel>
              <Select label="Day" value={filterDay} onChange={(e) => setFilterDay(e.target.value)}>
                <MenuItem value="">All Days</MenuItem>
                {Array.from({ length: 7 }, (_, i) => (
                  <MenuItem key={i + 1} value={String(i + 1)}>On Day {i + 1}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" fullWidth>
            <InputLabel>Tags</InputLabel>
            <Select
              label="Tags"
              multiple
              value={filterTags}
              onChange={(e) => {
                const vals = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                if (vals.includes(ADD_TAG)) {
                  setNewTagName('');
                  setNewTagColor(TAG_COLORS[0]);
                  setAddTagError(null);
                  setShowAddTag(true);
                  return;
                }
                setFilterTags(vals);
              }}
              renderValue={(selected) => {
                if (!selected.length) return 'All Tags';
                return selected.map((id) => tagById.get(id)?.Name || id).join(', ');
              }}
            >
              {availableTags.map((t) => (
                <MenuItem key={t.Id} value={t.Id}>
                  <Checkbox
                    size="small"
                    checked={filterTags.includes(t.Id)}
                    sx={{ p: 0.5, mr: 1, '&.Mui-checked': { color: t.Color || '#ed8936' } }}
                  />
                  {t.Name}
                </MenuItem>
              ))}
              <MenuItem value={ADD_TAG} sx={{ color: '#ed8936', fontWeight: 600 }}>
                <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add tag
              </MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              fullWidth
              onClick={() => {
                setFilterType('');
                setFilterStage('');
                setFilterCampaign('');
                setFilterDay('');
                setFilterTags([]);
              }}
              sx={{ textTransform: 'none' }}
            >
              Clear
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setFiltersOpen(false)}
              sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
            >
              Done
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Filter chips summary */}
      {(filterType || filterStage || filterCampaign || filterTags.length > 0) && (
        <Box sx={{ mb: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {filterType && (
            <Chip
              label={filterType === 'buyer' ? 'Buyer' : 'Seller'}
              size="small"
              onDelete={() => setFilterType('')}
              sx={{ bgcolor: 'rgba(237, 137, 54, 0.15)', color: '#ed8936' }}
            />
          )}
          {filterStage && (
            <Chip
              label={STAGE_LABELS[filterStage as Stage]}
              size="small"
              onDelete={() => setFilterStage('')}
              sx={{ bgcolor: `${STAGE_COLORS[filterStage as Stage]}22`, color: STAGE_COLORS[filterStage as Stage] }}
            />
          )}
          {filterCampaign && (
            <Chip
              icon={<CampaignIcon sx={{ fontSize: '14px !important' }} />}
              label={filterCampaign === SEVEN_DAY ? '7-Day Engagement' : filterCampaign}
              size="small"
              onDelete={() => { setFilterCampaign(''); setFilterDay(''); }}
              sx={{ bgcolor: 'rgba(237, 137, 54, 0.15)', color: '#ed8936' }}
            />
          )}
          {filterCampaign === SEVEN_DAY && filterDay && (
            <Chip
              label={`On Day ${filterDay}`}
              size="small"
              onDelete={() => setFilterDay('')}
              sx={{ bgcolor: 'rgba(237, 137, 54, 0.15)', color: '#ed8936' }}
            />
          )}
          {filterTags.map((id) => {
            const t = tagById.get(id);
            if (!t) return null;
            return (
              <Chip
                key={id}
                label={t.Name}
                size="small"
                onDelete={() => setFilterTags((prev) => prev.filter((x) => x !== id))}
                sx={{ bgcolor: `${t.Color || '#ed8936'}22`, color: t.Color || '#ed8936' }}
              />
            );
          })}
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', alignSelf: 'center', ml: 1 }}>
            {total} result{total !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {/* Bulk enrol action bar */}
      {selectMode && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, p: 1.5, flexWrap: 'wrap',
          bgcolor: 'var(--color-surface)', borderRadius: 1, border: '1px solid #ed893655',
          position: 'sticky', top: 0, zIndex: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={selectedIds.size > 0 && !allSelected}
              onChange={toggleSelectAll}
              sx={{ '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#ed8936' } }}
            />
            <Typography variant="body2" fontWeight={600}>{selectedIds.size} selected</Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Enrol into</InputLabel>
            <Select value={enrollTarget} label="Enrol into" onChange={(e) => setEnrollTarget(e.target.value)}>
              <MenuItem value={SEVEN_DAY}>7-Day Engagement</MenuItem>
              {myCampaigns.map((c) => (
                <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            disabled={selectedIds.size === 0}
            onClick={() => setEnrollConfirm(true)}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            Enrol
          </Button>

          {availableTags.length > 0 && (
            <>
              <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: 'var(--color-border)', mx: 0.5 }} />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  multiple
                  displayEmpty
                  value={bulkTags}
                  onChange={(e) => setBulkTags(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) =>
                    selected.length === 0 ? (
                      'Add tags'
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((id) => {
                          const t = tagById.get(id);
                          if (!t) return null;
                          return (
                            <Chip
                              key={id}
                              label={t.Name}
                              size="small"
                              sx={{ height: 18, fontSize: '0.65rem', bgcolor: `${t.Color || '#ed8936'}22`, color: t.Color || '#ed8936' }}
                            />
                          );
                        })}
                      </Box>
                    )
                  }
                >
                  {availableTags.map((t) => (
                    <MenuItem key={t.Id} value={t.Id}>
                      <Checkbox
                        size="small"
                        checked={bulkTags.includes(t.Id)}
                        sx={{ p: 0.5, mr: 1, '&.Mui-checked': { color: t.Color || '#ed8936' } }}
                      />
                      <Chip
                        label={t.Name}
                        size="small"
                        sx={{ bgcolor: `${t.Color || '#ed8936'}22`, color: t.Color || '#ed8936', fontWeight: 700 }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                disabled={selectedIds.size === 0 || bulkTags.length === 0 || bulkTagBusy}
                onClick={runBulkTag}
                sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
              >
                {bulkTagBusy ? 'Tagging...' : 'Add Tags'}
              </Button>
            </>
          )}
        </Box>
      )}

      {showMap ? (
        <ClientsMap
          filters={{
            search: search || undefined,
            type: filterType || undefined,
            stage: filterStage || undefined,
            campaign: filterCampaign || undefined,
            engagementDay: filterCampaign === SEVEN_DAY && filterDay ? Number(filterDay) : undefined,
            tags: filterTags.length > 0 ? filterTags : undefined,
            adminMode,
          }}
        />
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : clients.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography sx={{ color: 'var(--color-text-faint)', mb: 2 }}>
            {filterCampaign ? 'No clients enrolled in this campaign' : 'No clients found'}
          </Typography>
          {!filterCampaign && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setShowCreate(true)} sx={{ textTransform: 'none' }}>
              Create your first client
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {clients.map((client) => {
            const engagementOn = !!(client.EngagementActive ?? false);
            const blockEngagementOn = !engagementOn && !sevenDayReadiness.canActivate;
            const tags = client.Tags || [];
            const visibleTags = isMobile ? tags.slice(0, 2) : tags;
            const overflowTagCount = isMobile ? Math.max(0, tags.length - 2) : 0;
            return (
            <Card
              key={client.Id}
              sx={{
                bgcolor: 'var(--color-surface)',
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.05)' },
              }}
              onClick={() => selectMode ? toggleSelected(client.Id) : navigate(`/super-connector/clients/${client.Id}`)}
            >
              <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                  gap: isMobile ? 1 : 2,
                  py: 1.5,
                  '&:last-child': { pb: 1.5 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, width: '100%' }}>
                {selectMode && (
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(client.Id)}
                    onChange={() => toggleSelected(client.Id)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ p: 0.5, '&.Mui-checked': { color: '#ed8936' } }}
                  />
                )}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: '#ed8936',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    flexShrink: 0,
                  }}
                >
                  {client.Name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                    <Typography variant="body1" fontWeight={600} noWrap sx={{ color: 'var(--color-text-strong)' }}>
                      {client.Name}
                    </Typography>
                    {!isMobile && (
                      <>
                    <Chip
                      label={STAGE_LABELS[client.Stage as Stage] || client.Stage}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        bgcolor: `${STAGE_COLORS[client.Stage as Stage] || '#888'}22`,
                        color: STAGE_COLORS[client.Stage as Stage] || '#888',
                      }}
                    />
                    {client.Type && (
                      <Chip
                        label={client.Type === 'buyer' ? 'Buyer' : 'Seller'}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    )}
                    {client.WhatsAppUnreachableAt && (
                      <Tooltip title={WHATSAPP_UNREACHABLE_HINT}>
                        <Chip
                          icon={<PhonelinkEraseIcon sx={{ fontSize: '0.8rem' }} />}
                          label="Invalid WhatsApp Number"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            flexShrink: 0,
                            bgcolor: '#ef444422',
                            color: '#ef4444',
                            '& .MuiChip-icon': { color: '#ef4444', ml: 0.75, mr: -0.25 },
                          }}
                        />
                      </Tooltip>
                    )}
                    {tags.map((t) => (
                      <Chip
                        key={t.Id}
                        label={t.Name}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          bgcolor: `${t.Color || '#ed8936'}22`,
                          color: t.Color || '#ed8936',
                        }}
                      />
                    ))}
                      </>
                    )}
                    {isMobile && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCardMenu({ client, anchor: e.currentTarget });
                        }}
                        sx={{ ml: 'auto', color: 'var(--color-text-faint)' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                    {formatMobile(client.Mobile) || 'No phone'}
                    {!isMobile && client.Budget ? ` | Budget: $${Number(client.Budget).toLocaleString()}` : ''}
                  </Typography>
                  {!isMobile && client.CompanyName && (
                    <Typography variant="caption" noWrap sx={{ display: 'block', color: 'var(--color-text-subtle)', fontWeight: 600 }}>
                      {client.CompanyName}
                    </Typography>
                  )}
                </Box>

                {!isMobile && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={
                      blockEngagementOn
                        ? readinessWarningText(sevenDayReadiness.reasons)
                        : engagementOn ? '7-Day Engagement ON' : '7-Day Engagement OFF'
                    }>
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
                        <AutorenewIcon
                          fontSize="small"
                          sx={{
                            color: engagementOn ? '#ed8936' : 'var(--color-text-faint)',
                            mr: 0.25,
                          }}
                        />
                        <Switch
                          size="small"
                          checked={engagementOn}
                          disabled={blockEngagementOn}
                          onChange={(_, checked) => {
                            if (checked && !sevenDayReadiness.canActivate) {
                              setToast({ msg: readinessWarningText(sevenDayReadiness.reasons), severity: 'error' });
                              return;
                            }
                            setEngagementConfirm({ client, nextActive: checked });
                          }}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed8936' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ed8936' },
                          }}
                        />
                      </Box>
                    </Tooltip>
                    {client.Mobile && (
                      <Tooltip title="Send WhatsApp">
                        <IconButton
                          size="small"
                          onClick={() => setWaTarget(client)}
                          sx={{ color: '#25D366' }}
                        >
                          <WhatsAppIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete client">
                      <IconButton
                        size="small"
                        onClick={() => { setDeleteError(null); setDeleteConfirm(client); }}
                        sx={{ color: 'var(--color-text-faint)', '&:hover': { color: '#ef4444' } }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <EngagementDayProgress
                    lastSentDay={client.EngagementLastSentDayNumber ?? 0}
                    active={engagementOn}
                  />
                </Box>
                )}
                </Box>

                {isMobile && (
                  <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                      <Chip
                        label={STAGE_LABELS[client.Stage as Stage] || client.Stage}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          bgcolor: `${STAGE_COLORS[client.Stage as Stage] || '#888'}22`,
                          color: STAGE_COLORS[client.Stage as Stage] || '#888',
                        }}
                      />
                      {visibleTags.map((t) => (
                        <Chip
                          key={t.Id}
                          label={t.Name}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: `${t.Color || '#ed8936'}22`,
                            color: t.Color || '#ed8936',
                          }}
                        />
                      ))}
                      {overflowTagCount > 0 && (
                        <Chip label={`+${overflowTagCount}`} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title={
                        blockEngagementOn
                          ? readinessWarningText(sevenDayReadiness.reasons)
                          : engagementOn ? '7-Day Engagement ON' : '7-Day Engagement OFF'
                      }>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AutorenewIcon
                            fontSize="small"
                            sx={{ color: engagementOn ? '#ed8936' : 'var(--color-text-faint)', mr: 0.25 }}
                          />
                          <Switch
                            size="small"
                            checked={engagementOn}
                            disabled={blockEngagementOn}
                            onChange={(_, checked) => {
                              if (checked && !sevenDayReadiness.canActivate) {
                                setToast({ msg: readinessWarningText(sevenDayReadiness.reasons), severity: 'error' });
                                return;
                              }
                              setEngagementConfirm({ client, nextActive: checked });
                            }}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed8936' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ed8936' },
                            }}
                          />
                        </Box>
                      </Tooltip>
                      <EngagementDayProgress
                        lastSentDay={client.EngagementLastSentDayNumber ?? 0}
                        active={engagementOn}
                      />
                    </Box>
                    {blockEngagementOn && (
                      <Typography variant="caption" sx={{ color: '#ef4444' }}>
                        {readinessWarningText(sevenDayReadiness.reasons)}
                      </Typography>
                    )}
                  </>
                )}

              </CardContent>
            </Card>
            );
          })}

      <Menu
        anchorEl={cardMenu?.anchor}
        open={!!cardMenu}
        onClose={() => setCardMenu(null)}
        onClick={(e) => e.stopPropagation()}
      >
        {cardMenu?.client.Mobile && (
          <MenuItem onClick={() => { setWaTarget(cardMenu.client); setCardMenu(null); }}>
            <ListItemIcon><WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} /></ListItemIcon>
            <ListItemText>Send WhatsApp</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { setDeleteError(null); setDeleteConfirm(cardMenu!.client); setCardMenu(null); }}>
          <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>
          <ListItemText>Delete client</ListItemText>
        </MenuItem>
      </Menu>
        </Box>
      )}

      {/* Pagination — server-side, PAGE_SIZE rows per page */}
      {viewMode === 'list' && !loading && total > PAGE_SIZE && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mt: 2.5, mb: isMobile ? '124px' : 0 }}>
          <Pagination
            count={pageCount}
            page={page + 1}
            onChange={(_, p) => { setPage(p - 1); window.scrollTo({ top: 0 }); }}
            color="primary"
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
          />
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </Typography>
        </Box>
      )}

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          onClick={() => setShowCreate(true)}
          sx={{
            position: 'fixed',
            bottom: `calc(56px + var(--sc-bottom-banner-height, 0px))`,
            right: 24,
            bgcolor: '#ed8936',
            color: '#fff',
            '&:hover': { bgcolor: '#dd7826' },
          }}
        >
          <AddIcon />
        </Fab>
      )}

      <QuickCreateClient
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleCreated}
        highlight={spotlight === 'add-client'}
      />

      <ImportContactsDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={(r) => {
          setToast({
            msg: `Imported ${r.created} contact${r.created === 1 ? '' : 's'}${r.skipped ? `, ${r.skipped} skipped (duplicates)` : ''}`,
            severity: 'success',
          });
          loadClients();
        }}
      />

      {/* Shared compose dialog for row-level WhatsApp sends */}
      <WhatsAppComposeDialog
        open={!!waTarget}
        client={waTarget}
        onClose={() => setWaTarget(null)}
        onSent={() => loadClients()}
      />

      {/* Quick create tag (from the tag-filter dropdown) */}
      <Dialog
        open={showAddTag}
        onClose={() => !addTagBusy && setShowAddTag(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle fontWeight={700}>New tag</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Tag name"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runCreateTag(); }}
            sx={{ mt: 1 }}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
            {TAG_COLORS.map((c) => (
              <Box
                key={c}
                onClick={() => setNewTagColor(c)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: c,
                  cursor: 'pointer',
                  border: newTagColor === c ? '3px solid var(--color-text-strong)' : '3px solid transparent',
                }}
              />
            ))}
          </Box>
          {newTagName.trim() && (
            <Box sx={{ mt: 2 }}>
              <Chip
                label={newTagName.trim()}
                size="small"
                sx={{ bgcolor: `${newTagColor}22`, color: newTagColor, fontWeight: 700 }}
              />
            </Box>
          )}
          {addTagError && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#ef4444' }}>
              {addTagError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowAddTag(false)} disabled={addTagBusy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={addTagBusy || !newTagName.trim()}
            onClick={runCreateTag}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            {addTagBusy ? 'Creating...' : 'Create Tag'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete client confirmation */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => !deleteBusy && setDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle fontWeight={700}>Delete client?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>
            {deleteConfirm
              ? `Are you sure you want to delete ${deleteConfirm.Name}? This action cannot be undone.`
              : ''}
          </Typography>
          {deleteError && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#ef4444' }}>
              {deleteError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteConfirm(null)}
            disabled={deleteBusy}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={deleteBusy}
            onClick={async () => {
              if (!deleteConfirm) return;
              setDeleteBusy(true);
              setDeleteError(null);
              try {
                await deleteClient(deleteConfirm.Id);
                setClients((prev) => prev.filter((c) => c.Id !== deleteConfirm.Id));
                setTotal((prev) => Math.max(0, prev - 1));
                setDeleteConfirm(null);
              } catch (e) {
                console.error(e);
                setDeleteError(e instanceof Error ? e.message : 'Failed to delete');
              } finally {
                setDeleteBusy(false);
              }
            }}
            sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, textTransform: 'none' }}
          >
            {deleteBusy ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm dialog for the per-row engagement toggle */}
      <Dialog
        open={!!engagementConfirm}
        onClose={() => !engagementBusy && setEngagementConfirm(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle fontWeight={700}>
          {engagementConfirm?.nextActive ? 'Turn ON 7-Day Engagement?' : 'Turn OFF 7-Day Engagement?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>
            {engagementConfirm?.nextActive
              ? `${engagementConfirm?.client.Name} will start receiving the 7-day engagement messages from where they left off (one message per day, sent during your configured window).`
              : `${engagementConfirm?.client.Name} will stop receiving automated 7-day engagement messages. You can resume any time.`}
          </Typography>
          {engagementConfirm?.nextActive && !sevenDayReadiness.canActivate && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              {readinessWarningText(sevenDayReadiness.reasons)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEngagementConfirm(null)}
            disabled={engagementBusy}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={engagementBusy}
            onClick={async () => {
              if (!engagementConfirm) return;
              setEngagementBusy(true);
              try {
                await setClientEngagementActive(engagementConfirm.client.Id, engagementConfirm.nextActive);
                // Optimistic local update so the row reflects the new state immediately.
                setClients((prev) => prev.map((c) =>
                  c.Id === engagementConfirm.client.Id
                    ? { ...c, EngagementActive: engagementConfirm.nextActive }
                    : c,
                ));
                setEngagementConfirm(null);
              } catch (e) {
                console.error(e);
              } finally {
                setEngagementBusy(false);
              }
            }}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            {engagementBusy ? 'Saving...' : engagementConfirm?.nextActive ? 'Turn ON' : 'Turn OFF'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk enrol confirmation */}
      <Dialog
        open={enrollConfirm}
        onClose={() => !enrollBusy && setEnrollConfirm(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle fontWeight={700}>Enrol into {enrollTargetLabel}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>
            {enrollTarget === SEVEN_DAY
              ? `${selectedIds.size} client${selectedIds.size === 1 ? '' : 's'} will start receiving the 7-Day Engagement (one message per day during your configured window).`
              : `${selectedIds.size} client${selectedIds.size === 1 ? '' : 's'} will be added to "${enrollTargetLabel}". They'll start from Day 1 on the next daily run. Existing recipients are kept.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEnrollConfirm(false)} disabled={enrollBusy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={enrollBusy}
            onClick={runEnroll}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            {enrollBusy ? 'Enrolling...' : 'Enrol'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
